import { Helmet } from "react-helmet-async";

const DEFAULT = {
  siteName: "Xpeditionz NZ",
  title: "Xpeditionz — Premium Overland Gear NZ · Bushtech · EEZI-AWN · Quick Pitch",
  description:
    "New Zealand's home for genuine, world-class overland equipment — Bushtech aluminium ute canopies, EEZI-AWN K9 roof racks & roof tents, Quick Pitch awnings, and the Bush Cabin ute camper. Engineered in South Africa. Proven globally.",
  url: "https://www.xpeditionz.co.nz",
  image:
    "https://static.wixstatic.com/media/731077_aa9875e427f945f3b7a751933aed51db~mv2.jpg/v1/fill/w_1200,h_630,al_c,q_85,enc_avif,quality_auto/731077_aa9875e427f945f3b7a751933aed51db~mv2.jpg",
};

export const SEO = ({ title, description, path = "/", image, jsonLd, noindex = false }) => {
  const fullTitle = title ? `${title} · ${DEFAULT.siteName}` : DEFAULT.title;
  const desc = description || DEFAULT.description;
  const url = `${DEFAULT.url}${path}`;
  const img = image || DEFAULT.image;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={DEFAULT.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Structured data (schema.org) — powers Google price/availability rich snippets */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
};

/** Builds a schema.org Product JSON-LD object from a standard product detail model. */
export const buildProductJsonLd = ({ name, description, image, price, priceOnRequest, inStock, url }) => {
  const availability = priceOnRequest
    ? "https://schema.org/PreOrder"
    : inStock === false
      ? "https://schema.org/OutOfStock"
      : "https://schema.org/InStock";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image: image?.startsWith("http") ? image : `${DEFAULT.url}${image}`,
    brand: { "@type": "Brand", name: "Xpeditionz" },
    offers: {
      "@type": "Offer",
      priceCurrency: "NZD",
      availability,
      url: `${DEFAULT.url}${url}`,
    },
  };
  if (!priceOnRequest && price) jsonLd.offers.price = price;
  return jsonLd;
};

/** Builds a schema.org BreadcrumbList — crumbs is an array of {name, path}, path omitted for the current/last page. */
export const buildBreadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.name,
    ...(crumb.path ? { item: `${DEFAULT.url}${crumb.path}` } : {}),
  })),
});

/** Organization / LocalBusiness structured data — identity signal for Google
 * (knowledge panel eligibility, local relevance). Included once, on the homepage. */
export const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Xpeditionz",
  url: DEFAULT.url,
  logo: `${DEFAULT.url}/logos/xpeditionz-logo.png`,
  image: DEFAULT.image,
  telephone: "+64284349101",
  email: "support@xpeditionz.co.nz",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Christchurch",
    addressCountry: "NZ",
  },
  sameAs: [
    "https://www.instagram.com/xpeditionz.co.nz/",
    "https://www.facebook.com/Xpeditionz/",
  ],
});
